import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import { Pressable, ScrollView, StyleSheet } from "react-native";

import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";

interface Props {
  onSelectTime: (hour: number, minute: number) => void;
  selectedDate: DateTime | null;
  selectedHour: null | number;
  selectedMinute: null | number;
}

const ROW_HEIGHT = 48;

function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  const m = minute.toString().padStart(2, "0");
  return `${h}:${m} ${period}`;
}

// Generate all 96 slots
const TIME_SLOTS: { hour: number; minute: number }[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push({ hour: h, minute: m });
  }
}

export function TimePickerList({
  onSelectTime,
  selectedDate,
  selectedHour,
  selectedMinute,
}: Props) {
  const scrollRef = useRef<ScrollView>(null);
  const now = DateTime.local();
  const isToday =
    selectedDate !== null &&
    selectedDate.hasSame(now, "day") &&
    selectedDate.hasSame(now, "month") &&
    selectedDate.hasSame(now, "year");

  useEffect(() => {
    let targetIndex: number;
    if (selectedHour !== null && selectedMinute !== null) {
      targetIndex = TIME_SLOTS.findIndex(
        (s) => s.hour === selectedHour && s.minute === selectedMinute,
      );
    } else {
      // Default to 6 PM
      targetIndex = TIME_SLOTS.findIndex(
        (s) => s.hour === 18 && s.minute === 0,
      );
    }
    if (targetIndex >= 0) {
      // Slight delay so layout is ready
      setTimeout(() => {
        scrollRef.current?.scrollTo({
          animated: false,
          y: Math.max(0, targetIndex * ROW_HEIGHT - ROW_HEIGHT * 2),
        });
      }, 50);
    }
  }, [selectedHour, selectedMinute]);

  return (
    <ScrollView
      ref={scrollRef}
      showsVerticalScrollIndicator={false}
      style={styles.list}
    >
      {TIME_SLOTS.map(({ hour, minute }) => {
        const isSelected = hour === selectedHour && minute === selectedMinute;
        const isPast =
          isToday &&
          (hour < now.hour || (hour === now.hour && minute <= now.minute));
        return (
          <Pressable
            key={`${hour}-${minute}`}
            onPress={() => !isPast && onSelectTime(hour, minute)}
            style={[styles.row, isSelected && styles.rowSelected]}
          >
            <GText
              style={[
                styles.rowText,
                isSelected && styles.rowTextSelected,
                isPast && styles.rowTextPast,
              ]}
            >
              {formatTime(hour, minute)}
            </GText>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  list: {
    maxHeight: ROW_HEIGHT * 7,
  },
  row: {
    alignItems: "center",
    borderRadius: 8,
    height: ROW_HEIGHT,
    justifyContent: "center",
    marginVertical: 2,
  },
  rowSelected: {
    backgroundColor: colors.white,
  },
  rowText: {
    color: colors.white,
    fontSize: 16,
  },
  rowTextPast: {
    color: colors.subtle,
  },
  rowTextSelected: {
    color: colors.black,
    fontWeight: "600",
  },
});
